import type { Difficulty, Operation, Question, RoundResult } from "./types";

export type KumonLevel = "3A" | "2A" | "A" | "B";

export type KumonCategoryId =
  | "kumon_3a"
  | "kumon_2a"
  | "kumon_a_add"
  | "kumon_a_sub"
  | "kumon_a_mix"
  | "kumon_b_add_easy"
  | "kumon_b_add_medium"
  | "kumon_b_add_hard"
  | "kumon_b_sub_easy"
  | "kumon_b_sub_medium"
  | "kumon_b_sub_hard";

export interface KumonCategoryPreset {
  id: KumonCategoryId;
  level: KumonLevel;
  subMode?: string;
  difficulty?: Difficulty;
  label: string;
  subLabel: string;
  description: string;
  operations: Operation[];
  badgeColor?: string;
}

export const KUMON_CATEGORIES: KumonCategoryPreset[] = [
  {
    id: "kumon_3a",
    level: "3A",
    label: "3A",
    subLabel: "บวกไม่เกิน +3",
    description: "+1 (ตัวตั้ง≤1000), +2 (ตัวตั้ง≤32), +3 (ตัวตั้ง≤12)",
    operations: ["add"],
    badgeColor: "#ec4899",
  },
  {
    id: "kumon_2a",
    level: "2A",
    label: "2A",
    subLabel: "บวกไม่เกิน +10 (ผลบวก ≤ 20)",
    description: "โจทย์การบวกตัวบวกไม่เกิน 10 และผลลัพธ์ไม่เกิน 20",
    operations: ["add"],
    badgeColor: "#8b5cf6",
  },
  {
    id: "kumon_a_add",
    level: "A",
    subMode: "add",
    label: "A · บวก",
    subLabel: "บวกเลขสองหลัก (ผลบวก ≤ 28)",
    description: "บวกเลขสองหลักโดยผลบวกทั้งหมดไม่เกิน 28",
    operations: ["add"],
    badgeColor: "#3b82f6",
  },
  {
    id: "kumon_a_sub",
    level: "A",
    subMode: "sub",
    label: "A · ลบ",
    subLabel: "ลบเลขตัวตั้ง ≤ 20 (ตัวลบ -1 ถึง -5)",
    description: "ตัวตั้งไม่เกิน 20 ลบด้วย 1, 2, 3, 4, หรือ 5",
    operations: ["sub"],
    badgeColor: "#06b6d4",
  },
  {
    id: "kumon_a_mix",
    level: "A",
    subMode: "mix",
    label: "A · ผสม",
    subLabel: "บวกสองหลัก + ลบเลข 1-5",
    description: "โจทย์ผสมทั้งบวกผลบวก≤28 และลบตัวตั้ง≤20",
    operations: ["add", "sub"],
    badgeColor: "#10b981",
  },
  {
    id: "kumon_b_add_easy",
    level: "B",
    subMode: "add",
    difficulty: "easy",
    label: "B · บวก (ง่าย)",
    subLabel: "ผลบวกถึง 100 มีทด 1 ครั้ง",
    description: "โจทย์บวกผลลัพธ์ไม่เกิน 100 และมีการทด 1 ครั้ง",
    operations: ["add"],
    badgeColor: "#f59e0b",
  },
  {
    id: "kumon_b_add_medium",
    level: "B",
    subMode: "add",
    difficulty: "medium",
    label: "B · บวก (ปานกลาง)",
    subLabel: "บวกเลขสองหลัก มีทด 1-2 ครั้ง",
    description: "โจทย์บวกเลข 2 หลัก + 2 หลัก มีการทด 1-2 ครั้ง",
    operations: ["add"],
    badgeColor: "#f97316",
  },
  {
    id: "kumon_b_add_hard",
    level: "B",
    subMode: "add",
    difficulty: "hard",
    label: "B · บวก (ยาก)",
    subLabel: "บวกเลข 3 หลัก มีทด 1-3 ครั้ง",
    description: "โจทย์บวกเลข 3 หลัก มีการทด 1-3 ครั้ง",
    operations: ["add"],
    badgeColor: "#ef4444",
  },
  {
    id: "kumon_b_sub_easy",
    level: "B",
    subMode: "sub",
    difficulty: "easy",
    label: "B · ลบ (ง่าย)",
    subLabel: "ตัวตั้ง ≤ 20 ลบไม่เกิน -9",
    description: "ตัวตั้งไม่เกิน 20 ลบด้วยเลข 1 หลัก (1-9)",
    operations: ["sub"],
    badgeColor: "#14b8a6",
  },
  {
    id: "kumon_b_sub_medium",
    level: "B",
    subMode: "sub",
    difficulty: "medium",
    label: "B · ลบ (ปานกลาง)",
    subLabel: "ลบเลข 2 หลัก ต้องยืม 1 ครั้ง",
    description: "ลบเลข 2 หลักที่มีการกระจาย/ยืมหลักหน่วย 1 ครั้ง",
    operations: ["sub"],
    badgeColor: "#6366f1",
  },
  {
    id: "kumon_b_sub_hard",
    level: "B",
    subMode: "sub",
    difficulty: "hard",
    label: "B · ลบ (ยาก)",
    subLabel: "ลบเลข 3 หลัก ต้องยืม 1-2 ครั้ง",
    description: "ลบเลข 3 หลักที่มีการกระจาย/ยืม 1-2 ครั้ง",
    operations: ["sub"],
    badgeColor: "#a855f7",
  },
];

export interface KumonSelectedCategory {
  categoryId: KumonCategoryId;
}

export interface KumonGameConfig {
  mode: "solo" | "duel";
  selectedCategory: KumonCategoryId;
  player1Name: string;
  player2Name: string;
}
